import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { cohortWorkspaceApi } from "./cohortWorkspaceApi";
import type { Cohort, CohortProgram, CohortWorkspaceApi } from "./types";

export const COHORT_WORKSPACE_STORAGE_KEY = "dyen.cohortWorkspace.cohortId";

interface CohortWorkspaceValue {
  cohorts: Cohort[];
  cohortId: string | null;
  cohort: Cohort | null;
  cohortProgram: CohortProgram | null;
  loading: boolean;
  error: Error | null;
  setCohortId: (cohortId: string) => void;
  setCohortProgram: (cohortProgram: CohortProgram | null) => void;
  reload: () => Promise<void>;
}

interface CohortWorkspaceProviderProps {
  children: ReactNode;
  api?: CohortWorkspaceApi;
  storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
}

const CohortWorkspaceContext = createContext<CohortWorkspaceValue | undefined>(undefined);

const getBrowserStorage = () => (typeof window === "undefined" ? undefined : window.localStorage);

export function CohortWorkspaceProvider({
  children,
  api = cohortWorkspaceApi,
  storage = getBrowserStorage(),
}: CohortWorkspaceProviderProps) {
  const [cohorts, setCohorts] = useState<Cohort[]>([]);
  const [cohortId, setCohortIdState] = useState<string | null>(null);
  const [cohortProgram, setCohortProgramState] = useState<CohortProgram | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const canonicalCohorts = await api.listCohorts();
      const restoredId = storage?.getItem(COHORT_WORKSPACE_STORAGE_KEY) ?? null;
      const restoredIsValid = canonicalCohorts.some(({ _id }) => _id === restoredId);
      const preferred = canonicalCohorts.find(({ status }) => status === "active")
        ?? canonicalCohorts.find(({ status }) => status === "accepting_applications")
        ?? canonicalCohorts[0];
      const nextId = restoredIsValid ? restoredId : preferred?._id ?? null;

      setCohorts(canonicalCohorts);
      setCohortIdState(nextId);
      setCohortProgramState((current) =>
        current?.cohortId === nextId ? current : null,
      );

      if (nextId) storage?.setItem(COHORT_WORKSPACE_STORAGE_KEY, nextId);
      else storage?.removeItem(COHORT_WORKSPACE_STORAGE_KEY);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error("Failed to load cohorts"));
    } finally {
      setLoading(false);
    }
  }, [api, storage]);

  useEffect(() => {
    void load();
  }, [load]);

  const setCohortId = useCallback((nextId: string) => {
    if (!cohorts.some(({ _id }) => _id === nextId)) return;

    setCohortIdState(nextId);
    setCohortProgramState((current) =>
      current?.cohortId === nextId ? current : null,
    );
    storage?.setItem(COHORT_WORKSPACE_STORAGE_KEY, nextId);
  }, [cohorts, storage]);

  const setCohortProgram = useCallback((nextProgram: CohortProgram | null) => {
    if (nextProgram && nextProgram.cohortId !== cohortId) return;
    setCohortProgramState(nextProgram);
  }, [cohortId]);

  const cohort = useMemo(
    () => cohorts.find(({ _id }) => _id === cohortId) ?? null,
    [cohortId, cohorts],
  );

  const value = useMemo<CohortWorkspaceValue>(() => ({
    cohorts,
    cohortId,
    cohort,
    cohortProgram,
    loading,
    error,
    setCohortId,
    setCohortProgram,
    reload: load,
  }), [cohorts, cohortId, cohort, cohortProgram, loading, error, setCohortId, setCohortProgram, load]);

  return (
    <CohortWorkspaceContext.Provider value={value}>
      {children}
    </CohortWorkspaceContext.Provider>
  );
}

export function useCohortWorkspace() {
  const context = useContext(CohortWorkspaceContext);
  if (!context) throw new Error("useCohortWorkspace must be used within CohortWorkspaceProvider");
  return context;
}

export default CohortWorkspaceContext;
